import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDotPlayerConfig } from '../config/AppConfig';
import spotlightService from '../services/SpotlightService';
import './TrailerWatchPage.css';

/**
 * TrailerWatchPage Component
 * 
 * Simplified watch page specifically for playing trailers.
 * - No watch point tracking
 * - No continue watching
 * - Simple back button to return
 */
const TrailerWatchPage = () => {
  const { channelSlug, trailerVideoId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [channelData, setChannelData] = useState(null);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  
  const playerRef = useRef(null);
  const mountedRef = useRef(true);

  // Fetch channel data for title display
  useEffect(() => {
    const fetchChannelData = async () => {
      if (!channelSlug) {return;}
      
      try {
        const response = await spotlightService.getChannelBySlug(channelSlug);
        if (response?.channels?.[0]) {
          setChannelData(response.channels[0]);
        }
      } catch (err) {
        console.warn('TrailerWatchPage: Could not fetch channel data:', err);
        // Don't set error - we can still play the trailer without channel data
      }
    };

    fetchChannelData();
  }, [channelSlug]);

  // Mount the DotPlayer for trailer playback
  useEffect(() => {
    if (!trailerVideoId) {
      setError('No trailer video ID provided');
      setLoading(false);
      return;
    }

    mountedRef.current = true;

    const mountPlayer = async () => {
      // Wait for DOM and DotPlayer to be ready
      await new Promise(resolve => setTimeout(resolve, 200));

      if (!mountedRef.current || !window.DotPlayer || !playerRef.current) {
        if (!window.DotPlayer) {
          setError('Video player not available');
          setLoading(false);
        }
        return;
      }

      try {
        // Unmount any existing DotPlayer instance first
        if (window.DotPlayer.el) {
          try {
            window.DotPlayer.unmount();
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (e) {
            // No existing player to unmount
          }
        }

        // Get DotPlayer configuration - trailers don't have ads
        const playerConfig = await getDotPlayerConfig(trailerVideoId, '#trailer-player', {
          autostart: true,
          controls: true,
          muted: false,
          playsInline: true,
          fluid: true,
          aspectRatio: '16:9',
          skin: true,
          pip: false,
          theme: {
            fontColor: '#f2f2f2',
            fontColorHover: '#FCC834',
            progressSliderMain: '#FCC834',
            progressSliderBackground: '#B4B7BB',
            controlBar: '#000000',
          },
        }, { adsEnabled: false });

        // Mount the player
        const player = await window.DotPlayer.mount(playerConfig);

        if (mountedRef.current) {
          console.log('🎬 TrailerWatchPage: Waiting for player to load...');
          await player.isPlayerLoaded();
          console.log('✅ TrailerWatchPage: Player loaded successfully');
          setPlayerLoaded(true);
          setLoading(false);
        }
      } catch (err) {
        console.error('❌ TrailerWatchPage: Player mount error:', err);
        if (mountedRef.current) {
          setError('Failed to load trailer');
          setLoading(false);
        }
      }
    };

    mountPlayer();

    return () => {
      mountedRef.current = false;
      if (window.DotPlayer) {
        try {
          window.DotPlayer.unmount();
        } catch (e) {
          console.warn('TrailerWatchPage: Unmount error (ignored):', e);
        }
      }
      setPlayerLoaded(false);
    };
  }, [trailerVideoId]);

  // Handle back button click
  const handleBack = () => {
    navigate(-1); // Go back to previous page (PDP modal will reopen)
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (error) {
    return (
      <div className="trailer-watch-page trailer-error">
        <div className="trailer-error-content">
          <h2>Unable to Play Trailer</h2>
          <p>{error}</p>
          <button className="trailer-back-button" onClick={handleBack}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="trailer-watch-page">
      {/* Header with back button and title */}
      <div className="trailer-header">
        <button className="trailer-back-button" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="trailer-title">
          {channelData?.title && (
            <span className="trailer-movie-title">{channelData.title}</span>
          )}
          <span className="trailer-label">Trailer</span>
        </div>
      </div>

      {/* Player container */}
      <div className="trailer-player-container">
        {loading && (
          <div className="trailer-loading">
            <div className="trailer-spinner"></div>
            <p>Loading trailer...</p>
          </div>
        )}
        <div 
          id="trailer-player" 
          ref={playerRef}
          className={`trailer-player ${playerLoaded ? 'loaded' : ''}`}
        />
      </div>
    </div>
  );
};

export default TrailerWatchPage;
